import { FC } from 'react';

import { useFormikContext } from 'formik';
import cn from 'classnames';
import { HiChevronLeft } from 'react-icons/hi';
import dayjs from 'dayjs';
import {
  CLIENT_BOOKING_FORM,
  CLIENT_BOOKING_STEPS,
} from 'modules/account/calendar/forms/schema';
import { Button } from 'ui/components/common';
import convertTime from 'modules/account/calendar/utils/convertTime';

import { PUBLIC_BOOKING_DETAILS } from './ClientPublicBooking';

type PROPS = {
  publicBookingDetails: PUBLIC_BOOKING_DETAILS;
  setStep: React.Dispatch<React.SetStateAction<string | null>>;
};

const ClientPublicBookingReview: FC<PROPS> = ({
  publicBookingDetails,
  setStep,
}) => {
  const { values, handleSubmit, isSubmitting } =
    useFormikContext<CLIENT_BOOKING_FORM>();

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'max-w-[700px]',
        'w-full',
        'p-8',
        'rounded-[6px]',
        'bg-fill-tertiary',
        'shadow-leapify-sm',
      )}
    >
      <div className="space-y-4 border-b-[0.5px] border-b-separator-primary pb-4 font-semibold">
        <button
          type="button"
          onClick={() => setStep(CLIENT_BOOKING_STEPS.form)}
          className={cn(
            'inline-flex',
            'gap-1',
            'items-center',
            'text-xs',
            'hover:text-primary-base',
            'transition',
          )}
        >
          <HiChevronLeft type="button" className="h-5 w-5" />
          Booking Details
        </button>
        <div className="text-[22px] font-bold leading-7 tracking-[0.88px]">
          Confirm Your Details
        </div>
      </div>
      <div className="mt-4 space-y-4 border-b-[0.5px] border-b-separator-primary pb-4">
        <div
          className={cn(
            'py-[18px]',
            'px-[22px]',
            'rounded-[6px]',
            'border',
            'border-separator-secondary',
            'space-y-4',
          )}
        >
          <p className="text-sm font-bold leading-[22px] tracking-[0.56px] text-info-base">
            Meeting Details
          </p>
          <div className="grid grid-cols-2 gap-x-5 gap-y-4">
            <div className="col-span-1">
              <p className="text-sm font-semibold text-content">
                Booking Title
              </p>
              <p className="text-sm font-bold text-secondary-dark">
                {publicBookingDetails?.title}
              </p>
            </div>
            <div className="col-span-1">
              <p className="text-sm font-semibold text-content">
                Booking Meeting Duration
              </p>
              <p className="text-sm font-bold text-secondary-dark">
                {publicBookingDetails?.duration} Minutes
              </p>
            </div>
            <div className="col-span-2">
              <p className="text-sm font-semibold text-content">Description</p>
              <p className="text-sm font-bold text-secondary-dark">
                {publicBookingDetails?.content}
              </p>
            </div>
          </div>
        </div>
        <div
          className={cn(
            'py-[18px]',
            'px-[22px]',
            'rounded-[6px]',
            'border',
            'border-separator-secondary',
            'space-y-4',
          )}
        >
          <p className="text-sm font-bold leading-[22px] tracking-[0.56px] text-info-base">
            Attendee Details
          </p>
          <div className="grid grid-cols-2 gap-x-5 gap-y-4">
            <div className="col-span-1">
              <p className="text-sm font-semibold text-content">Guest Name</p>
              <p className="text-sm font-bold text-secondary-dark">
                {publicBookingDetails?.guest?.fullName}
              </p>
            </div>
            <div className="w-full">
              <p className="text-sm font-semibold text-content">
                Date Time Selected
              </p>
              <p className="text-sm font-bold text-secondary-dark">
                {convertTime(values?.startAt as string)} -{' '}
                {dayjs(values?.selectedDate).format('dddd')}
              </p>
              <p className="text-sm font-bold text-secondary-dark">
                {dayjs(values?.selectedDate).format('DD MMM YYYY')}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-[18px]">
        <Button
          intent={'tertiary'}
          className="font-bold"
          disabled={isSubmitting}
          onClick={() => setStep(CLIENT_BOOKING_STEPS.form)}
          label="BACK"
        />
        <Button
          type="submit"
          className="font-bold"
          disabled={isSubmitting}
          label="CONFIRM DETAILS"
        />
      </div>
    </form>
  );
};

export default ClientPublicBookingReview;
